'use client';

/**
 * Chat in the browser (docs/CHAT.md; backend routes/chat.ts).
 *
 * One store for the whole page, shared by the navbar's chat button and the
 * chat window (components/chat/*):
 *   counts    unread messages and waiting requests, for the red badge
 *   rooms     the list, newest first
 *   threads   each open chat's messages (loaded on demand, older pages as you scroll up)
 *   dock      the window: open, full window, which chat or view; kept for the
 *             tab (sessionStorage) so moving between pages keeps it as it was
 *
 * Live: one EventSource per page (`/api/chat/stream`). It reconnects on its own;
 * after a reconnect the list and the open chat are reloaded, so nothing sent in
 * between is missed. Sending is optimistic: the message shows at once marked
 * "sending", carries a clientId (a retry never posts twice), and is replaced by
 * the server's copy, or marked failed with a Retry.
 */

import { useSyncExternalStore } from 'react';
import type { Person } from './campsApi';

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

export interface ChatMessage {
  id: string; roomId: string; seq: number; kind: 'text' | 'system'; author: Person | null;
  text: string; deleted: boolean; editedAt: string | null; replyTo: number | null; clientId: string | null; at: string;
  /** Only in the browser, before the server has it. */
  pending?: boolean; failed?: boolean;
}
/** What two people already share (shown as a badge; it does not decide who may chat). */
export interface Bond { games: number; clears: number; score: number; lastGame: string; lastAt: string | null; connected: boolean }
/** Where a chat with one person stands for me. */
export type ChatWith = 'active' | 'sent' | 'received' | 'declined' | null;
export type Contact = Person & { bond: Bond | null; chat: ChatWith; roomId: string | null };
export interface ChatRoom {
  bond: Bond | null;
  id: string; kind: 'dm' | 'group'; state: 'active' | 'request' | 'declined'; incoming: boolean; outgoing: boolean;
  name: string; people: Person[]; memberCount: number; role: 'owner' | 'admin' | 'member'; muted: boolean;
  seq: number; readSeq: number; otherRead: number; unread: number;
  last: { seq: number; text: string; authorName: string; kind: string; at: string; mine: boolean } | null; lastAt: string;
  members?: { person: Person; role: 'owner' | 'admin' | 'member' }[];
}
export type DockView = 'list' | 'room' | 'new' | 'group' | 'settings' | 'request' | 'prefs';
export interface Dock { open: boolean; full: boolean; view: DockView; room: string | null; tab: 'chats' | 'requests'; to: Person | null }

export class ChatApiError extends Error { constructor(message: string, public status: number, public code?: string) { super(message); } }

async function call<T>(path: string, init?: { method?: string; json?: unknown }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}/api${path}`, {
      credentials: 'include', cache: 'no-store',
      method: init?.method ?? (init?.json !== undefined ? 'POST' : 'GET'),
      headers: init?.json !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
    });
  } catch { throw new ChatApiError('Could not reach the server. Check your connection.', 0); }
  let body: any = {};
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok) throw new ChatApiError(body?.error || `That did not work (${res.status}).`, res.status, body?.code);
  return body as T;
}

export const api = {
  rooms: () => call<{ rooms: ChatRoom[] }>('/chat/rooms').then((r) => r.rooms),
  room: (id: string) => call<{ room: ChatRoom }>(`/chat/rooms/${id}`).then((r) => r.room),
  messages: (id: string, before?: number) => call<{ messages: ChatMessage[]; more: boolean }>(`/chat/rooms/${id}/messages${before ? `?before=${before}` : ''}`),
  send: (id: string, b: { text: string; clientId: string; replyTo?: number | null }) => call<{ message: ChatMessage }>(`/chat/rooms/${id}/messages`, { json: b }).then((r) => r.message),
  read: (id: string, seq: number) => call(`/chat/rooms/${id}/read`, { json: { seq } }),
  typing: (id: string) => call(`/chat/rooms/${id}/typing`, { json: {} }),
  accept: (id: string) => call(`/chat/rooms/${id}/accept`, { json: {} }),
  decline: (id: string) => call(`/chat/rooms/${id}/decline`, { json: {} }),
  leave: (id: string) => call(`/chat/rooms/${id}/leave`, { json: {} }),
  mute: (id: string, muted: boolean) => call(`/chat/rooms/${id}/mute`, { json: { muted } }),
  rename: (id: string, name: string) => call(`/chat/rooms/${id}`, { method: 'PATCH', json: { name } }),
  addMembers: (id: string, userIds: string[]) => call(`/chat/rooms/${id}/members`, { json: { userIds } }),
  removeMember: (id: string, userId: string) => call(`/chat/rooms/${id}/members/${userId}`, { method: 'DELETE' }),
  setAdmin: (id: string, userId: string, on: boolean) => call(`/chat/rooms/${id}/admins/${userId}`, { json: { on } }),
  edit: (mid: string, text: string) => call<{ message: ChatMessage }>(`/chat/messages/${mid}`, { method: 'PATCH', json: { text } }).then((r) => r.message),
  remove: (mid: string) => call<{ message: ChatMessage }>(`/chat/messages/${mid}`, { method: 'PATCH', json: { delete: true } }).then((r) => r.message),
  report: (mid: string, reason: string) => call(`/chat/messages/${mid}/report`, { json: { reason } }),
  dm: (userId: string, text?: string) => call<{ roomId: string; created: boolean }>('/chat/dm', { json: { userId, text } }),
  group: (name: string, members: string[]) => call<{ roomId: string; skipped: number }>('/chat/groups', { json: { name, members } }),
  contacts: (q: string) => call<{ people: Contact[] }>(`/chat/contacts?q=${encodeURIComponent(q)}`).then((r) => r.people),
  peer: (userId: string) => call<{ person: Person; bond: Bond | null; chat: ChatWith; roomId: string | null }>(`/chat/people/${userId}`),
  block: (userId: string) => call('/chat/blocks', { json: { userId } }),
  blocked: () => call<{ blocked: Person[] }>('/chat/blocks').then((r) => r.blocked),
  unblock: (userId: string) => call(`/chat/blocks/${userId}`, { method: 'DELETE' }),
  unread: () => call<{ unread: number; requests: number }>('/chat/unread'),
};

// ── The store ────────────────────────────────────────────────────────────────

interface Thread { list: ChatMessage[]; more: boolean; loading: boolean }
interface State {
  me: string | null;
  live: boolean;
  unread: number;
  requests: number;
  rooms: ChatRoom[] | null;
  threads: Record<string, Thread>;
  typing: Record<string, { name: string; until: number }>;
  dock: Dock;
  /** The chat window's own look: light, dark, or as the site (auto). */
  theme: ChatTheme;
}
export type ChatTheme = 'light' | 'dark' | 'auto';

const DOCK_KEY = 'kh_chat_dock';
/**
 * The chat window's own look. 'auto' (the default) follows Kharma's light/dark
 * setting (lib/theme.tsx, `.dark` on <html>); light/dark override it for the
 * chat only. v2: picks saved under the first key (made while the chat was being
 * built) are dropped, so everyone starts out following the site (owner, 2026-10-15).
 */
const THEME_KEY = 'kh_chat_theme2';
const OLD_THEME_KEY = 'kh_chat_theme';
const closed: Dock = { open: false, full: false, view: 'list', room: null, tab: 'chats', to: null };
let state: State = { me: null, live: false, unread: 0, requests: 0, rooms: null, threads: {}, typing: {}, dock: closed, theme: 'auto' };
const subs = new Set<() => void>();
const set = (patch: Partial<State> | ((s: State) => Partial<State>)) => {
  state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
  subs.forEach((f) => f());
};
const subscribe = (f: () => void) => { subs.add(f); return () => subs.delete(f); };

export function useChat<T>(pick: (s: State) => T): T {
  return useSyncExternalStore(subscribe, () => pick(state), () => pick({ ...state, dock: closed, theme: 'auto' }));
}
export const chatState = () => state;

// ── Live ─────────────────────────────────────────────────────────────────────

let es: EventSource | null = null;
let slow: ReturnType<typeof setInterval> | null = null;
const later = (() => {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  return (key: string, fn: () => void, ms = 250) => { clearTimeout(timers.get(key)); timers.set(key, setTimeout(fn, ms)); };
})();

export async function refreshCounts() {
  try { const c = await api.unread(); set({ unread: c.unread, requests: c.requests }); } catch { /* next time */ }
}
export async function refreshRooms() {
  try {
    const rooms = await api.rooms();
    set((s) => ({ rooms: rooms.map((r) => ({ ...r, members: s.rooms?.find((x) => x.id === r.id)?.members })) }));
  } catch { /* next time */ }
}

/** Start chat for this signed-in account (once per page; a different account restarts it). */
export function startChat(userId: string | null) {
  if (state.me === userId) return;
  stopChat();
  if (!userId) return;
  let dock = closed;
  try { const d = JSON.parse(sessionStorage.getItem(DOCK_KEY) || 'null'); if (d && typeof d === 'object') dock = { ...closed, ...d, to: null }; } catch { /* private mode */ }
  let theme: ChatTheme = 'auto';
  try {
    localStorage.removeItem(OLD_THEME_KEY);
    const t = localStorage.getItem(THEME_KEY); if (t === 'light' || t === 'dark') theme = t;
  } catch { /* private mode */ }
  set({ me: userId, dock, rooms: null, threads: {}, typing: {}, theme });
  void refreshCounts();
  if (dock.open) void refreshRooms();
  es = new EventSource(`${API}/api/chat/stream`, { withCredentials: true });
  es.addEventListener('open', () => {
    set({ live: true });
    // After a reconnect, catch up on anything sent while we were away.
    void refreshCounts();
    if (state.rooms) void refreshRooms();
    if (state.dock.room && state.threads[state.dock.room]) void loadLatest(state.dock.room);
  });
  es.addEventListener('error', () => set({ live: false }));
  es.addEventListener('hello', (e) => { const c = JSON.parse((e as MessageEvent).data); set({ unread: c.unread, requests: c.requests }); });
  es.addEventListener('message', (e) => onMessage(JSON.parse((e as MessageEvent).data).message));
  es.addEventListener('edit', (e) => { const m: ChatMessage = JSON.parse((e as MessageEvent).data).message; upsert(m); });
  es.addEventListener('read', (e) => {
    const d = JSON.parse((e as MessageEvent).data);
    if (d.userId === state.me) { later('counts', refreshCounts); return; }
    set((s) => ({ rooms: s.rooms?.map((r) => (r.id === d.roomId ? { ...r, otherRead: Math.max(r.otherRead, d.seq) } : r)) ?? null }));
  });
  es.addEventListener('typing', (e) => {
    const d = JSON.parse((e as MessageEvent).data);
    set((s) => ({ typing: { ...s.typing, [d.roomId]: { name: d.name, until: Date.now() + 4000 } } }));
    later(`typing:${d.roomId}`, () => set((s) => { const t = { ...s.typing }; delete t[d.roomId]; return { typing: t }; }), 4200);
  });
  es.addEventListener('room', () => { later('rooms', () => { if (state.rooms) void refreshRooms(); void refreshCounts(); }, 300); });
  // Belt and braces: the counts once a minute, in case the stream is down.
  slow = setInterval(() => { if (!state.live) void refreshCounts(); }, 60_000);
}

export function stopChat() {
  es?.close(); es = null;
  if (slow) clearInterval(slow);
  slow = null;
  state = { me: null, live: false, unread: 0, requests: 0, rooms: null, threads: {}, typing: {}, dock: closed, theme: state.theme };
  subs.forEach((f) => f());
}

function upsert(m: ChatMessage) {
  set((s) => {
    const t = s.threads[m.roomId];
    if (!t) return {};
    const i = t.list.findIndex((x) => x.id === m.id || (!!m.clientId && x.clientId === m.clientId));
    const list = i >= 0 ? t.list.map((x, j) => (j === i ? m : x)) : [...t.list, m].sort((a, b) => a.seq - b.seq);
    return { threads: { ...s.threads, [m.roomId]: { ...t, list } } };
  });
}

function onMessage(m: ChatMessage) {
  upsert(m);
  set((s) => { const t = { ...s.typing }; delete t[m.roomId]; return { typing: t }; });
  // Reading it now? Then it is read.
  if (viewing(m.roomId) && m.author?.id !== state.me) void markRead(m.roomId, m.seq);
  else later('counts', refreshCounts, 150);
}

/** Is this chat on screen right now (window open, this chat showing, the tab visible)? */
const viewing = (roomId: string) => state.dock.open && state.dock.view === 'room' && state.dock.room === roomId && typeof document !== 'undefined' && document.visibilityState === 'visible';

// ── Actions ──────────────────────────────────────────────────────────────────

export function setDock(patch: Partial<Dock>) {
  const dock = { ...state.dock, ...patch };
  set({ dock });
  try { sessionStorage.setItem(DOCK_KEY, JSON.stringify({ ...dock, to: null })); } catch { /* private mode */ }
  if (dock.open && !state.rooms) void refreshRooms();
}
/** The chat window's look, kept in this browser. */
export function setChatTheme(theme: ChatTheme) {
  set({ theme });
  try { if (theme === 'auto') localStorage.removeItem(THEME_KEY); else localStorage.setItem(THEME_KEY, theme); } catch { /* private mode */ }
}
export const toggleDock = () => setDock({ open: !state.dock.open });
export const openRoom = (id: string) => { setDock({ open: true, view: 'room', room: id }); void loadLatest(id); };

/** Open a chat with this person: your chat if there is one, else a chat request to write. */
export async function chatWith(person: Person) {
  try {
    const r = await api.dm(person.id);
    await refreshRooms();
    openRoom(r.roomId);
  } catch (e) {
    if (e instanceof ChatApiError && e.code === 'request-needed') setDock({ open: true, view: 'request', to: person });
    else throw e;
  }
}

export async function loadLatest(roomId: string) {
  set((s) => ({ threads: { ...s.threads, [roomId]: { list: s.threads[roomId]?.list ?? [], more: s.threads[roomId]?.more ?? false, loading: true } } }));
  try {
    const r = await api.messages(roomId);
    set((s) => {
      const pending = (s.threads[roomId]?.list ?? []).filter((m) => m.pending || m.failed);
      return { threads: { ...s.threads, [roomId]: { list: [...r.messages, ...pending], more: r.more, loading: false } } };
    });
    const last = r.messages[r.messages.length - 1];
    if (last && viewing(roomId)) void markRead(roomId, last.seq);
  } catch {
    set((s) => ({ threads: { ...s.threads, [roomId]: { ...(s.threads[roomId] ?? { list: [], more: false }), loading: false } } }));
  }
}

export async function loadOlder(roomId: string) {
  const t = state.threads[roomId];
  if (!t || t.loading || !t.more) return;
  const first = t.list.find((m) => !m.pending);
  if (!first) return;
  set((s) => ({ threads: { ...s.threads, [roomId]: { ...t, loading: true } } }));
  try {
    const r = await api.messages(roomId, first.seq);
    set((s) => {
      const cur = s.threads[roomId]?.list ?? [];
      const have = new Set(cur.map((m) => m.id));
      return { threads: { ...s.threads, [roomId]: { list: [...r.messages.filter((m) => !have.has(m.id)), ...cur], more: r.more, loading: false } } };
    });
  } catch { set((s) => ({ threads: { ...s.threads, [roomId]: { ...t, loading: false } } })); }
}

export async function markRead(roomId: string, seq: number) {
  set((s) => ({ rooms: s.rooms?.map((r) => (r.id === roomId ? { ...r, readSeq: Math.max(r.readSeq, seq), unread: Math.max(0, r.seq - seq) } : r)) ?? null }));
  try { await api.read(roomId, seq); } catch { /* the next read covers it */ }
  later('counts', refreshCounts, 100);
}

const newId = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

export async function sendMessage(roomId: string, text: string, replyTo: number | null, retryOf?: ChatMessage) {
  const clientId = retryOf?.clientId ?? newId();
  const me = state.rooms?.flatMap((r) => r.members ?? []).find((m) => m.person.id === state.me)?.person ?? null;
  const temp: ChatMessage = {
    id: `tmp-${clientId}`, roomId, seq: Number.MAX_SAFE_INTEGER, kind: 'text', author: me ?? (state.me ? { id: state.me, name: '', avatar: null, demo: false, level: 0, rank: '', kharma: 0 } : null),
    text, deleted: false, editedAt: null, replyTo, clientId, at: new Date().toISOString(), pending: true,
  };
  upsert(temp);
  try {
    const m = await api.send(roomId, { text, clientId, replyTo });
    upsert(m);
    set((s) => ({ rooms: s.rooms?.map((r) => (r.id === roomId ? { ...r, seq: Math.max(r.seq, m.seq), readSeq: Math.max(r.readSeq, m.seq), unread: 0, last: { seq: m.seq, text: m.text, authorName: m.author?.name ?? '', kind: 'text', at: m.at, mine: true }, lastAt: m.at } : r)).sort((a, b) => +new Date(b.lastAt) - +new Date(a.lastAt)) ?? null }));
    return m;
  } catch (e) {
    upsert({ ...temp, pending: false, failed: true });
    throw e;
  }
}

let lastTyping = 0;
export function sayTyping(roomId: string) {
  if (Date.now() - lastTyping < 2500) return;
  lastTyping = Date.now();
  void api.typing(roomId).catch(() => undefined);
}

export function dropFailed(roomId: string, m: ChatMessage) {
  set((s) => { const t = s.threads[roomId]; return t ? { threads: { ...s.threads, [roomId]: { ...t, list: t.list.filter((x) => x.id !== m.id) } } } : {}; });
}
export function applyEdited(m: ChatMessage) { upsert(m); }

/** A room's display name: the group's name, or the other person. */
export const roomTitle = (r: ChatRoom) => (r.kind === 'group' ? r.name || 'Group' : r.people[0] ? `u/${r.people[0].name}` : 'Chat');
